import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ProfileService } from '../../services/profile.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

type ContentMenuElement = {
  name: string;
  count: number;
};

@Component({
  selector: 'app-contents',
  imports: [TranslateModule, UpperFirstLetterPipe, RouterOutlet],
  templateUrl: './contents.html',
  styleUrl: './contents.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Contents {
  private readonly router: Router = inject(Router);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  public readonly profileService: ProfileService = inject(ProfileService);
  activeElementName: WritableSignal<string | undefined> = signal(undefined);

  readonly scenicCount: Signal<number> = computed<number>(
    () => this.profileService.profile()?.videos_count ?? 0,
  );
  readonly workoutCount: Signal<number> = computed<number>(
    () => this.profileService.profile()?.workout_count ?? 0,
  );
  readonly gpsTrackCount: Signal<number> = computed<number>(
    () => this.profileService.profile()?.gps_only_count ?? 0,
  );
  readonly coachingCount: Signal<number> = computed<number>(
    () => this.profileService.profile()?.videos_coaching_count ?? 0,
  );
  readonly playlistCount: Signal<number> = computed<number>(
    () => this.profileService.profile()?.playlists_count ?? 0,
  );
  readonly menuElements: Signal<
    {
      name: string;
      count: number;
    }[]
  > = computed(() => [
    { name: 'scenic', count: this.scenicCount() },
    { name: 'coaching', count: this.coachingCount() },
    { name: 'workout', count: this.workoutCount() },
    { name: 'gps-only', count: this.gpsTrackCount() },
    // { name: 'playlist', count: this.playlistCount() },
    // { name: 'planning', count: 0 },
  ]);

  constructor() {
    this.updateActiveElementFromUrl(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => this.updateActiveElementFromUrl(event.urlAfterRedirects));
  }

  setActiveElement(element: ContentMenuElement): void {
    this.activeElementName.set(element.name);
    this.router.navigate(['/contents', element.name]);
  }

  private updateActiveElementFromUrl(url: string): void {
    const path: string = url.split('?')[0].split('#')[0];
    const activeElement: ContentMenuElement | undefined = this.menuElements().find(
      (element: ContentMenuElement) =>
        path === `/contents/${element.name}` || path.startsWith(`/contents/${element.name}/`),
    );

    this.activeElementName.set(activeElement?.name);
  }
}
